import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@memorang/ui";
import { View } from "react-native";
import { Icon, Text } from "react-native-paper";

import type { ScenarioImageSource } from "../data/scenarios";
import { ScenarioMarkdown } from "./ScenarioMarkdown";
import { ScenarioSummary } from "./ScenarioSummary";

const SectionLabel = ({
	children,
	icon,
}: { children: string; icon: string }) => {
	const theme = useAppTheme();
	return (
		<View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
			<Icon source={icon} size={15} color={theme.colors.onSurfaceVariant} />
			<Text
				variant="labelSmall"
				style={{
					color: theme.colors.onSurfaceVariant,
					textTransform: "uppercase",
					letterSpacing: 1,
				}}
			>
				{children}
			</Text>
		</View>
	);
};

// Highlighted callout for the "what you'll do next" instruction, mirroring the
// Lesson card's tinted-surface + accent-border treatment so directions read as a
// distinct, actionable block.
const UpNextCallout = ({ text }: { text: string }) => {
	const theme = useAppTheme();
	return (
		<View
			style={{
				backgroundColor: theme.colors.surfaceVariant,
				borderRadius: theme.roundness,
				borderLeftWidth: 4,
				borderLeftColor: theme.colors.primary,
				padding: 16,
				gap: 8,
			}}
		>
			<View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
				<Icon
					source="message-text-outline"
					size={15}
					color={theme.colors.primary}
				/>
				<Text
					variant="labelSmall"
					style={{
						color: theme.colors.primary,
						textTransform: "uppercase",
						letterSpacing: 1,
					}}
				>
					Up Next
				</Text>
			</View>
			<ScenarioMarkdown text={text} fontSize={15} />
		</View>
	);
};

/**
 * Practice step layout: a short info lead-in, the case scenario inside a labelled
 * card (with any example image slotted between its paragraphs), and the upcoming
 * conversation instruction as a highlighted callout - so the four pieces read as
 * distinct blocks instead of one run-on body.
 */
export const ScenarioPractice = ({
	info,
	scenario,
	instruction,
	media = [],
}: {
	info?: string;
	scenario: string;
	instruction?: string;
	media?: ScenarioImageSource[];
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const paragraphs = scenario
		.split(/\n{2,}/)
		.map((paragraph) => paragraph.trim())
		.filter(Boolean);

	return (
		<View style={{ gap: 16, paddingHorizontal: isMobile ? 16 : 0 }}>
			{info ? <ScenarioMarkdown text={info} fontSize={16} /> : null}
			<View
				style={{
					backgroundColor: theme.colors.elevation.level1,
					borderRadius: theme.roundness * 2,
					borderWidth: 1,
					borderColor: theme.colors.outlineVariant,
					padding: 16,
					gap: 8,
				}}
			>
				<SectionLabel icon="file-document-outline">Scenario</SectionLabel>
				<ScenarioSummary paragraphs={paragraphs} media={media} fontSize={16} />
			</View>
			{instruction ? <UpNextCallout text={instruction} /> : null}
		</View>
	);
};
